/**
 * @fileoverview Letterhead fragment resolution helpers
 *
 * Purpose:
 * - Resolve branding profile letterhead into reusable header/footer HTML fragments
 * - Prefer image-backed letterhead while keeping legacy HTML fallback support
 * - Resolve document layout padding from branding profile and per-template overrides
 *
 * Used by:
 * - Branding settings previews
 * - Document previews and exports
 */

export type PageRule =
  "all" | "firstOnly" | "notFirst" | "oddOnly" | "evenOnly" | "none";

export type ZoneKind = "header" | "footer" | "leftSide" | "rightSide";

/**
 * How a side-zone image occupies the page:
 *   - "fullHeight" — the historical behaviour: the image is stretched from
 *     the top to the bottom of the page and the body is uniformly narrowed
 *     beside it on every page the rule includes.
 *   - "topBlock"   — the image renders at its natural aspect ratio anchored
 *     to the TOP corner of the page (first page only) and body text WRAPS
 *     around it: narrow beside the block, full width below it. Built for
 *     letterheads whose side art only spans part of the page (MOR partial
 *     side letterhead).
 */
export type SideFit = "fullHeight" | "topBlock";

/**
 * Default fraction of the page height a "topBlock" side occupies when the
 * asset doesn't specify one (≈ the top third — matches the common
 * logo-and-address-block letterhead layout).
 */
export const DEFAULT_TOP_BLOCK_HEIGHT_PCT = 1 / 3;

/**
 * Clamp a topBlock height fraction to a sane range. Below 5% the block is
 * invisible; above 90% it's effectively a full-height band (use
 * "fullHeight" for that).
 */
export function clampTopBlockHeightPct(
  value: number | null | undefined,
): number {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return DEFAULT_TOP_BLOCK_HEIGHT_PCT;
  }
  return Math.max(0.05, Math.min(0.9, value));
}

/**
 * Source-image crop for a "topBlock" side, per edge, as fractions of the
 * SOURCE image's dimensions. Lets the user isolate the relevant section
 * of a full-height side strip (e.g. keep just the top logo/address block)
 * instead of squeezing the whole strip into the block.
 *
 * Unlike header/footer crops (capped at 0.45 per edge), topBlock crops
 * routinely need to discard MOST of the image — a full-page strip whose
 * artwork stops a third of the way down wants `bottom ≈ 0.65` — so each
 * edge allows up to 0.9, with the combined visible region floored at 10%.
 */
export interface SideCrop {
  top: number;
  bottom: number;
  left: number;
  right: number;
}

export const EMPTY_SIDE_CROP: Readonly<SideCrop> = {
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
};

function clampSideCropEdge(value: number | null | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(0.9, value));
}

/** Scale an opposing edge pair down proportionally if their sum exceeds 0.9. */
function scaleCropPair(a: number, b: number): [number, number] {
  if (a + b <= 0.9) return [a, b];
  const scale = 0.9 / (a + b);
  return [a * scale, b * scale];
}

/**
 * Normalize a topBlock side crop: clamp each edge, then scale opposing
 * pairs down proportionally if they'd leave less than 10% of the image
 * visible on that axis.
 */
export function normalizeSideCrop(
  crop:
    | {
        top?: number | null;
        bottom?: number | null;
        left?: number | null;
        right?: number | null;
      }
    | null
    | undefined,
): SideCrop {
  const [top, bottom] = scaleCropPair(
    clampSideCropEdge(crop?.top),
    clampSideCropEdge(crop?.bottom),
  );
  const [left, right] = scaleCropPair(
    clampSideCropEdge(crop?.left),
    clampSideCropEdge(crop?.right),
  );
  return { top, bottom, left, right };
}

/** True when any edge of a (normalized) side crop is non-zero. */
export function sideCropIsActive(crop: SideCrop): boolean {
  return crop.top > 0 || crop.bottom > 0 || crop.left > 0 || crop.right > 0;
}

/**
 * CSS geometry for rendering a cropped topBlock inside a fixed-size box:
 * the image is oversized so the visible (uncropped) region exactly fills
 * the box, then shifted up/left to hide the cropped edges. All values are
 * percentages of the BOX's own width/height, so the same numbers work for
 * `position:absolute` (export HTML) and inline-style React rendering.
 */
export function sideCropGeometry(crop: SideCrop): {
  imgWidthPct: number;
  imgHeightPct: number;
  offsetLeftPct: number;
  offsetTopPct: number;
} {
  const visibleX = Math.max(0.1, 1 - crop.left - crop.right);
  const visibleY = Math.max(0.1, 1 - crop.top - crop.bottom);
  return {
    imgWidthPct: 100 / visibleX,
    imgHeightPct: 100 / visibleY,
    offsetLeftPct: (crop.left / visibleX) * 100,
    offsetTopPct: (crop.top / visibleY) * 100,
  };
}

export interface LetterheadSource {
  headerImageUrl?: string | null;
  footerImageUrl?: string | null;
  /** Optional full-height vertical letterhead images (NEW v5) */
  leftSideImageUrl?: string | null;
  rightSideImageUrl?: string | null;
  headerHtml?: string | null;
  footerHtml?: string | null;
  headerPageRule?: PageRule | null;
  footerPageRule?: PageRule | null;
  leftSidePageRule?: PageRule | null;
  rightSidePageRule?: PageRule | null;
  /**
   * Render scale for header/footer images. `1.0` = natural width (100%).
   * Smaller values shrink the letterhead within its zone; larger values
   * are clamped at 100% so it never overflows the page.
   */
  headerImageScale?: number | null;
  footerImageScale?: number | null;
  /**
   * Render scale for side-band letterhead images. Applied as a horizontal
   * transform in exports so the band gets narrower while staying pinned
   * to the page edge.
   */
  leftSideImageScale?: number | null;
  rightSideImageScale?: number | null;
  /**
   * How each side image occupies the page. Absent/null means "fullHeight"
   * (the historical behaviour) so existing letterheads render unchanged.
   */
  leftSideFit?: SideFit | null;
  rightSideFit?: SideFit | null;
  /**
   * For "topBlock" sides: the fraction of the page height the block spans
   * (e.g. 0.35 = down to 35% of the page). Ignored for "fullHeight".
   */
  leftSideTopBlockHeightPct?: number | null;
  rightSideTopBlockHeightPct?: number | null;
  /**
   * For "topBlock" sides: source-image crop isolating the section of the
   * artwork the block should show (e.g. just the top logo/address block of
   * a full-height strip). Ignored for "fullHeight".
   */
  leftSideCrop?: {
    top?: number | null;
    bottom?: number | null;
    left?: number | null;
    right?: number | null;
  } | null;
  rightSideCrop?: {
    top?: number | null;
    bottom?: number | null;
    left?: number | null;
    right?: number | null;
  } | null;
  /**
   * Render-time crop for header/footer images, as a fraction of the
   * rendered image width (0–~0.45 per edge). Applied via an
   * `overflow:hidden` wrapper + negative margins.
   */
  headerImageCropTop?: number | null;
  headerImageCropBottom?: number | null;
  headerImageCropLeft?: number | null;
  headerImageCropRight?: number | null;
  footerImageCropTop?: number | null;
  footerImageCropBottom?: number | null;
  footerImageCropLeft?: number | null;
  footerImageCropRight?: number | null;
  /**
   * Page fit (see `utils/branding/pageFit.ts`): the blank fraction of the
   * page height above a page-fit header crop / below a page-fit footer
   * crop. `null`/absent means the zone is slot-fit (the historical
   * behaviour), so existing letterheads render unchanged.
   */
  headerPageFitTop?: number | null;
  footerPageFitBottom?: number | null;
}

export interface LetterheadFragments {
  headerHtml?: string;
  footerHtml?: string;
  /** Full-height vertical letterhead HTML fragments (NEW v5). */
  leftSideHtml?: string;
  rightSideHtml?: string;
  headerPageRule: PageRule;
  footerPageRule: PageRule;
  leftSidePageRule: PageRule;
  rightSidePageRule: PageRule;
  /** Clamped side-band scale factors (default 1) for the exporter to apply. */
  leftSideScale: number;
  rightSideScale: number;
}

const LETTERHEAD_IMAGE_STYLE = "display:block;width:100%;height:auto;";

/**
 * Clamp scale to a sensible range. 0.3 = 30%, 1.0 = natural (100%).
 * Upper bound is 1.0 — images already render at full container width,
 * so scaling above 100% would overflow the zone.
 */
function clampScale(scale: number | null | undefined): number {
  if (typeof scale !== "number" || !Number.isFinite(scale)) return 1;
  return Math.max(0.3, Math.min(1, scale));
}

/**
 * Clamp a render-time crop fraction. Crop is expressed as a fraction of the
 * rendered image *width* (so it maps cleanly to negative CSS margins, which
 * are width-relative). Capped at 0.45 so a zone can never fully collapse.
 */
function clampCrop(value: number | null | undefined): number {
  if (typeof value !== "number" || !Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(0.45, value));
}

export interface LetterheadCropGeometry {
  /** True when any edge is cropped. */
  cropped: boolean;
  /** Image width as a percentage of the (overflow:hidden) wrapper. */
  imgWidthPct: number;
  /** Negative-margin magnitudes as percentages of the wrapper width. */
  marginTopPct: number;
  marginBottomPct: number;
  marginLeftPct: number;
  marginRightPct: number;
}

/**
 * Compute the geometry for a render-time letterhead crop.
 *
 * The crop is rendered as an `overflow:hidden` wrapper containing an image
 * that is widened to `imgWidthPct` and pulled by negative margins so the
 * uncropped region exactly fills the wrapper. All values are derived purely
 * from the crop fractions — no intrinsic image dimensions are needed — so
 * the same geometry can drive the React preview and the exported HTML
 * identically. Margin percentages are width-relative (matching how the crop
 * fractions are defined).
 */
export function letterheadCropGeometry(
  cropTop: number = 0,
  cropBottom: number = 0,
  cropLeft: number = 0,
  cropRight: number = 0,
): LetterheadCropGeometry {
  const t = clampCrop(cropTop);
  const b = clampCrop(cropBottom);
  const l = clampCrop(cropLeft);
  const r = clampCrop(cropRight);
  // Visible horizontal fraction — floored so the wrapper can't collapse.
  const visibleWidth = Math.max(0.1, 1 - l - r);
  return {
    cropped: t > 0 || b > 0 || l > 0 || r > 0,
    imgWidthPct: 100 / visibleWidth,
    marginTopPct: (t / visibleWidth) * 100,
    marginBottomPct: (b / visibleWidth) * 100,
    marginLeftPct: (l / visibleWidth) * 100,
    marginRightPct: (r / visibleWidth) * 100,
  };
}

/**
 * Side-letterhead images span the full page height (top to bottom). The
 * image is rendered into a 100%-height container so it stretches over the
 * entire page in the PDF print path. Width is image-natural and read from
 * the image's intrinsic dimensions during measurement.
 */
const SIDE_LETTERHEAD_IMAGE_STYLE =
  "display:block;width:100%;height:100%;object-fit:fill;";

/**
 * Build a simple HTML fragment for a letterhead snapshot image.
 *
 * @example
 * const html = buildLetterheadImageHtml("https://example.com/header.png", "Header letterhead");
 */
export function buildLetterheadImageHtml(
  imageUrl: string,
  alt: string,
  scale: number = 1,
  cropTop: number = 0,
  cropBottom: number = 0,
  cropLeft: number = 0,
  cropRight: number = 0,
): string {
  const clamped = clampScale(scale);
  const geo = letterheadCropGeometry(cropTop, cropBottom, cropLeft, cropRight);
  const src = escapeHtmlAttribute(imageUrl);
  const altAttr = escapeHtmlAttribute(alt);

  // No crop — preserve the legacy plain-<img> output.
  if (!geo.cropped) {
    if (clamped >= 0.999) {
      return `<img src="${src}" alt="${altAttr}" style="${LETTERHEAD_IMAGE_STYLE}" />`;
    }
    const widthPct = (clamped * 100).toFixed(2);
    const style = `display:block;width:${widthPct}%;height:auto;margin:0 auto;`;
    return `<img src="${src}" alt="${altAttr}" style="${style}" />`;
  }

  // Cropped — wrap in an overflow:hidden box and pull the image with
  // negative margins. Margin percentages are relative to the wrapper's
  // width, which matches how the crop fractions are defined.
  const widthPct = clamped >= 0.999 ? "100" : (clamped * 100).toFixed(2);
  const wrapperStyle = `display:block;overflow:hidden;width:${widthPct}%;margin:0 auto;`;
  const imgStyle =
    `display:block;width:${geo.imgWidthPct.toFixed(3)}%;height:auto;` +
    (geo.marginTopPct > 0
      ? `margin-top:-${geo.marginTopPct.toFixed(3)}%;`
      : "") +
    (geo.marginBottomPct > 0
      ? `margin-bottom:-${geo.marginBottomPct.toFixed(3)}%;`
      : "") +
    (geo.marginLeftPct > 0
      ? `margin-left:-${geo.marginLeftPct.toFixed(3)}%;`
      : "") +
    (geo.marginRightPct > 0
      ? `margin-right:-${geo.marginRightPct.toFixed(3)}%;`
      : "");
  return `<div style="${wrapperStyle}"><img src="${src}" alt="${altAttr}" style="${imgStyle}" /></div>`;
}

/**
 * Build a side (full-height) letterhead image fragment.
 */
export function buildSideLetterheadImageHtml(
  imageUrl: string,
  alt: string,
): string {
  return `<img src="${escapeHtmlAttribute(imageUrl)}" alt="${escapeHtmlAttribute(alt)}" style="${SIDE_LETTERHEAD_IMAGE_STYLE}" />`;
}

/**
 * Resolve branding data into export/preview-safe fragments.
 *
 * @example
 * const fragments = resolveLetterheadFragments({
 *   headerImageUrl: "https://example.com/header.png",
 *   headerHtml: "<div>Legacy</div>",
 * });
 */
export function resolveLetterheadFragments(
  source: LetterheadSource | null | undefined,
): LetterheadFragments {
  const headerHtml = source?.headerImageUrl
    ? buildLetterheadImageHtml(
        source.headerImageUrl,
        "Header letterhead",
        source.headerImageScale ?? 1,
        source.headerImageCropTop ?? 0,
        source.headerImageCropBottom ?? 0,
        source.headerImageCropLeft ?? 0,
        source.headerImageCropRight ?? 0,
      )
    : source?.headerHtml || undefined;
  const footerHtml = source?.footerImageUrl
    ? buildLetterheadImageHtml(
        source.footerImageUrl,
        "Footer letterhead",
        source.footerImageScale ?? 1,
        source.footerImageCropTop ?? 0,
        source.footerImageCropBottom ?? 0,
        source.footerImageCropLeft ?? 0,
        source.footerImageCropRight ?? 0,
      )
    : source?.footerHtml || undefined;
  const leftSideHtml = source?.leftSideImageUrl
    ? buildSideLetterheadImageHtml(
        source.leftSideImageUrl,
        "Left side letterhead",
      )
    : undefined;
  const rightSideHtml = source?.rightSideImageUrl
    ? buildSideLetterheadImageHtml(
        source.rightSideImageUrl,
        "Right side letterhead",
      )
    : undefined;

  return {
    headerHtml,
    footerHtml,
    leftSideHtml,
    rightSideHtml,
    headerPageRule: source?.headerPageRule ?? "all",
    footerPageRule: source?.footerPageRule ?? "all",
    leftSidePageRule: source?.leftSidePageRule ?? "all",
    rightSidePageRule: source?.rightSidePageRule ?? "all",
    leftSideScale: clampScale(source?.leftSideImageScale ?? 1),
    rightSideScale: clampScale(source?.rightSideImageScale ?? 1),
  };
}

// ---------------------------------------------------------------------------
// Variant fragments — per-zone HTML + per-zone page rule for one variant.
//
// Multiple variants stack: on each page, for each zone, the highest-priority
// variant whose zone page-rule includes the page wins.
// ---------------------------------------------------------------------------

/**
 * Body side margins measured from a letterhead's SOURCE page (px at 96dpi,
 * from the page edge, or from a side band's inner edge). Saved on a page-fit
 * header/footer asset so the body column can follow the firm's own letter
 * instead of the app's default margins (MOR-1432). A side left unset
 * inherits the firm's margin for that edge.
 */
export interface SourceBodyMargins {
  left?: number;
  right?: number;
}

export interface VariantFragments {
  /** Optional id — used by the renderer for stable keying / debugging */
  id?: string;
  headerHtml?: string;
  footerHtml?: string;
  leftSideHtml?: string;
  rightSideHtml?: string;
  headerPageRule: PageRule;
  footerPageRule: PageRule;
  leftSidePageRule: PageRule;
  rightSidePageRule: PageRule;
  /** Clamped scale factors for the exporter (sides applied as width %). */
  leftSideScale: number;
  rightSideScale: number;
  /** Resolved side fit — "fullHeight" unless the source opts into "topBlock". */
  leftSideFit: SideFit;
  rightSideFit: SideFit;
  /** Clamped topBlock height fractions (only meaningful when fit is "topBlock"). */
  leftSideTopBlockHeightPct: number;
  rightSideTopBlockHeightPct: number;
  /** Normalized topBlock source crops (all-zero when uncropped/fullHeight). */
  leftSideCrop: SideCrop;
  rightSideCrop: SideCrop;
  /**
   * Raw letterhead image URLs (when image-backed). Carried alongside the
   * HTML fragments so non-HTML exporters (Word OOXML, image embedders) can
   * fetch the original image without re-parsing the fragment.
   */
  headerImageUrl?: string | null;
  footerImageUrl?: string | null;
  leftSideImageUrl?: string | null;
  rightSideImageUrl?: string | null;
  /** Per-variant padding overrides — `null` means "no opinion" */
  padding?: PaddingSource | null;
  /**
   * Asset ids behind each zone's artwork, so the paginator can derive a
   * page's layout key (which header / footer / sides are showing) and
   * look up the variant's per-layout margins.
   */
  headerAssetId?: string | null;
  footerAssetId?: string | null;
  leftSideAssetId?: string | null;
  rightSideAssetId?: string | null;
  /** The owning variant's per-layout margins (same list on every fragment). */
  layoutMargins?: LayoutMarginEntry[] | null;
  /** Page-fit offsets — see `LetterheadSource`. `null` means slot fit. */
  headerPageFitTop?: number | null;
  footerPageFitBottom?: number | null;
  /**
   * Body margins the zone's asset measured from its source page. Only
   * honoured while that zone is page-fit — see `letterheadBodyPaddingForPage`.
   */
  sourceBodyMargins?: SourceBodyMargins | null;
}

/**
 * Build per-zone fragments for one variant. Mirrors
 * resolveLetterheadFragments() but on a single variant; zones with no
 * content emit `undefined` HTML so pickVariantForPage() can skip them.
 */
export function resolveVariantFragments(
  variant: LetterheadSource & {
    id?: string;
    padding?: PaddingSource | null;
    headerAssetId?: string | null;
    footerAssetId?: string | null;
    leftSideAssetId?: string | null;
    rightSideAssetId?: string | null;
    layoutMargins?: LayoutMarginEntry[] | null;
    sourceBodyMargins?: SourceBodyMargins | null;
  },
): VariantFragments {
  const headerHtml = variant.headerImageUrl
    ? buildLetterheadImageHtml(
        variant.headerImageUrl,
        "Header letterhead",
        variant.headerImageScale ?? 1,
        variant.headerImageCropTop ?? 0,
        variant.headerImageCropBottom ?? 0,
        variant.headerImageCropLeft ?? 0,
        variant.headerImageCropRight ?? 0,
      )
    : variant.headerHtml || undefined;
  const footerHtml = variant.footerImageUrl
    ? buildLetterheadImageHtml(
        variant.footerImageUrl,
        "Footer letterhead",
        variant.footerImageScale ?? 1,
        variant.footerImageCropTop ?? 0,
        variant.footerImageCropBottom ?? 0,
        variant.footerImageCropLeft ?? 0,
        variant.footerImageCropRight ?? 0,
      )
    : variant.footerHtml || undefined;
  const leftSideHtml = variant.leftSideImageUrl
    ? buildSideLetterheadImageHtml(
        variant.leftSideImageUrl,
        "Left side letterhead",
      )
    : undefined;
  const rightSideHtml = variant.rightSideImageUrl
    ? buildSideLetterheadImageHtml(
        variant.rightSideImageUrl,
        "Right side letterhead",
      )
    : undefined;

  return {
    id: variant.id,
    headerHtml,
    footerHtml,
    leftSideHtml,
    rightSideHtml,
    headerPageRule: variant.headerPageRule ?? "all",
    footerPageRule: variant.footerPageRule ?? "all",
    leftSidePageRule: variant.leftSidePageRule ?? "all",
    rightSidePageRule: variant.rightSidePageRule ?? "all",
    leftSideScale: clampScale(variant.leftSideImageScale ?? 1),
    rightSideScale: clampScale(variant.rightSideImageScale ?? 1),
    leftSideFit: variant.leftSideFit ?? "fullHeight",
    rightSideFit: variant.rightSideFit ?? "fullHeight",
    leftSideTopBlockHeightPct: clampTopBlockHeightPct(
      variant.leftSideTopBlockHeightPct,
    ),
    rightSideTopBlockHeightPct: clampTopBlockHeightPct(
      variant.rightSideTopBlockHeightPct,
    ),
    leftSideCrop: normalizeSideCrop(variant.leftSideCrop),
    rightSideCrop: normalizeSideCrop(variant.rightSideCrop),
    headerImageUrl: variant.headerImageUrl ?? null,
    footerImageUrl: variant.footerImageUrl ?? null,
    leftSideImageUrl: variant.leftSideImageUrl ?? null,
    rightSideImageUrl: variant.rightSideImageUrl ?? null,
    padding: variant.padding ?? null,
    headerAssetId: variant.headerAssetId ?? null,
    footerAssetId: variant.footerAssetId ?? null,
    leftSideAssetId: variant.leftSideAssetId ?? null,
    rightSideAssetId: variant.rightSideAssetId ?? null,
    layoutMargins: variant.layoutMargins ?? null,
    headerPageFitTop: headerHtml ? (variant.headerPageFitTop ?? null) : null,
    footerPageFitBottom: footerHtml
      ? (variant.footerPageFitBottom ?? null)
      : null,
    sourceBodyMargins: variant.sourceBodyMargins ?? null,
  };
}

/**
 * Per-page page-rule evaluator — extends the legacy 5-rule vocabulary
 * with `"none"` (always false). Kept here so VariantFragments callers
 * don't need to import the exporter just to evaluate a rule.
 */
export function shouldRuleShowOnPage(
  rule: PageRule,
  pageIndex: number,
): boolean {
  switch (rule) {
    case "all":
      return true;
    case "firstOnly":
      return pageIndex === 1;
    case "notFirst":
      return pageIndex !== 1;
    case "oddOnly":
      return pageIndex % 2 === 1;
    case "evenOnly":
      return pageIndex % 2 === 0;
    case "none":
      return false;
    default:
      return true;
  }
}

function zoneRule(variant: VariantFragments, zone: ZoneKind): PageRule {
  switch (zone) {
    case "header":
      return variant.headerPageRule;
    case "footer":
      return variant.footerPageRule;
    case "leftSide":
      return variant.leftSidePageRule;
    case "rightSide":
      return variant.rightSidePageRule;
  }
}

function zoneHtml(
  variant: VariantFragments,
  zone: ZoneKind,
): string | undefined {
  switch (zone) {
    case "header":
      return variant.headerHtml;
    case "footer":
      return variant.footerHtml;
    case "leftSide":
      return variant.leftSideHtml;
    case "rightSide":
      return variant.rightSideHtml;
  }
}

/**
 * Pick the winning variant for a given zone on a given page.
 *
 * Iterates the variants in priority order (caller-supplied — typically
 * sorted by `order` ascending so highest-priority is first). The first
 * variant whose zone has both content AND a page rule that includes this
 * page wins. Variants with `"none"` for the zone are always skipped.
 *
 * Returns `null` when no variant supplies content for this zone on this
 * page — the renderer then leaves the zone empty (and falls back to the
 * default height reservation).
 */
export function pickVariantForPage(
  variants: VariantFragments[],
  zone: ZoneKind,
  pageIndex: number,
): VariantFragments | null {
  for (const variant of variants) {
    if (!zoneHtml(variant, zone)) continue;
    const rule = zoneRule(variant, zone);
    if (shouldRuleShowOnPage(rule, pageIndex)) return variant;
  }
  return null;
}

function escapeHtmlAttribute(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// ---------------------------------------------------------------------------
// Document padding resolution
// ---------------------------------------------------------------------------

export interface DocumentPadding {
  /** Top padding above the header zone (px) */
  headerTop: number;
  /** Left margin of the header zone (px) */
  headerLeft: number;
  /** Right margin of the header zone (px) */
  headerRight: number;
  /**
   * Symmetric view of the header side margins (px). Equals `headerLeft`
   * when both sides match; otherwise the SMALLER of the two. Kept for
   * callers that only need a single inset (Word baseline margins, debug
   * output) — layout code should read `headerLeft` / `headerRight`.
   */
  headerSide: number;
  /** Top padding above the body content (px) */
  bodyTop: number;
  /** Left margin of the body zone (px) */
  bodyLeft: number;
  /** Right margin of the body zone (px) */
  bodyRight: number;
  /** Gap between the end of the body column and the footer zone (px). */
  bodyBottom: number;
  /** Symmetric view of the body side margins — see `headerSide`. */
  bodySide: number;
  /** Bottom padding below the footer zone (px) */
  footerBottom: number;
  /** Left margin of the footer zone (px) */
  footerLeft: number;
  /** Right margin of the footer zone (px) */
  footerRight: number;
  /** Symmetric view of the footer side margins — see `headerSide`. */
  footerSide: number;
}

/** Which letterhead asset (or none) each zone shows on a page. */
export interface PageLayoutKey {
  header: string | null;
  footer: string | null;
  leftSide: string | null;
  rightSide: string | null;
}

/** A variant's margins for one combination of zones it produces. */
export interface LayoutMarginEntry {
  key: PageLayoutKey;
  padding: PaddingSource;
}

export interface PaddingSource {
  headerTopPadding?: number | null;
  /** Symmetric header side margin. Overridden per side by the Left/Right fields. */
  headerSidePadding?: number | null;
  headerLeftPadding?: number | null;
  headerRightPadding?: number | null;
  bodyTopPadding?: number | null;
  bodySidePadding?: number | null;
  bodyLeftPadding?: number | null;
  bodyRightPadding?: number | null;
  bodyBottomPadding?: number | null;
  footerBottomPadding?: number | null;
  footerSidePadding?: number | null;
  footerLeftPadding?: number | null;
  footerRightPadding?: number | null;
  /** @deprecated Use the per-zone side fields instead */
  sidePadding?: number | null;
}

export const DEFAULT_PADDING: Readonly<DocumentPadding> = {
  headerTop: 40,
  headerLeft: 56,
  headerRight: 56,
  headerSide: 56,
  bodyTop: 20,
  bodyLeft: 56,
  bodyRight: 56,
  bodyBottom: 0,
  bodySide: 56,
  footerBottom: 24,
  footerLeft: 56,
  footerRight: 56,
  footerSide: 56,
};

type PaddingZone = "header" | "body" | "footer";
type PaddingSideKey = `${PaddingZone}${"Left" | "Right" | "Side"}Padding`;

/**
 * The value a single tier contributes for one side of one zone. The
 * per-side field wins over the tier's symmetric field, and either beats the
 * legacy shared `sidePadding`. Returns `undefined` when the tier says
 * nothing about this side — the caller then falls through to the next tier.
 * Resolving per tier (rather than per field across all tiers) keeps a
 * higher tier's symmetric value authoritative over a lower tier's Left /
 * Right split.
 */
function tierSide(
  src: PaddingSource | null | undefined,
  zone: PaddingZone,
  side: "Left" | "Right",
): number | undefined {
  if (!src) return undefined;
  const perSide = src[`${zone}${side}Padding` as PaddingSideKey];
  if (perSide !== undefined && perSide !== null) return perSide;
  const symmetric = src[`${zone}SidePadding` as PaddingSideKey];
  if (symmetric !== undefined && symmetric !== null) return symmetric;
  const legacy = src.sidePadding;
  if (legacy !== undefined && legacy !== null) return legacy;
  return undefined;
}

/** Symmetric view of a left/right pair — see `DocumentPadding.headerSide`. */
export function symmetricSide(left: number, right: number): number {
  return Math.min(left, right);
}

/**
 * Resolve document padding in priority order (highest to lowest):
 *   1. `documentOverride` — per-job-document overrides
 *   2. `templateOverride` — per-template overrides
 *   3. `variantPadding` — per-variant overrides (highest-priority variant
 *      whose padding is set), followed by the body column the letterhead
 *      measured from its source page (`variantPaddingTiers`)
 *   4. `brandingProfile` — org-level defaults
 *   5. Hardcoded defaults
 *
 * Side margins resolve independently for the left and right edge of each
 * zone. Within a tier the per-side field (`headerLeftPadding`) beats the
 * symmetric field (`headerSidePadding`), which beats the legacy shared
 * `sidePadding`; the first tier that says anything about a side wins.
 *
 * The `variantPadding` argument accepts an *ordered* list of
 * PaddingSource (highest priority first) so the caller can pass the same
 * variant ordering it uses for fragment selection. The first variant
 * whose field is set wins for that field.
 *
 * @example
 * const padding = resolveDocumentPadding(brandingProfile, template, jobDoc, variants);
 */
export function resolveDocumentPadding(
  brandingProfile?: PaddingSource | null,
  templateOverride?: PaddingSource | null,
  documentOverride?: PaddingSource | null,
  variantPadding?: ReadonlyArray<PaddingSource | null | undefined> | null,
): DocumentPadding {
  const bp = brandingProfile;
  const tp = templateOverride;
  const dp = documentOverride;
  const variants = variantPadding ?? [];

  // Walk the ordered variant list to find the first variant with this field set
  function fromVariant<K extends keyof PaddingSource>(
    key: K,
  ): PaddingSource[K] | undefined {
    for (const v of variants) {
      if (v && v[key] !== undefined && v[key] !== null) return v[key];
    }
    return undefined;
  }

  function side(zone: PaddingZone, which: "Left" | "Right"): number {
    const fromDoc = tierSide(dp, zone, which);
    if (fromDoc !== undefined) return fromDoc;
    const fromTemplate = tierSide(tp, zone, which);
    if (fromTemplate !== undefined) return fromTemplate;
    for (const v of variants) {
      const fromV = tierSide(v, zone, which);
      if (fromV !== undefined) return fromV;
    }
    const fromBranding = tierSide(bp, zone, which);
    if (fromBranding !== undefined) return fromBranding;
    return DEFAULT_PADDING[`${zone}${which}`];
  }

  const headerLeft = side("header", "Left");
  const headerRight = side("header", "Right");
  const bodyLeft = side("body", "Left");
  const bodyRight = side("body", "Right");
  const footerLeft = side("footer", "Left");
  const footerRight = side("footer", "Right");

  return {
    headerTop:
      dp?.headerTopPadding ??
      tp?.headerTopPadding ??
      fromVariant("headerTopPadding") ??
      bp?.headerTopPadding ??
      DEFAULT_PADDING.headerTop,
    headerLeft,
    headerRight,
    headerSide: symmetricSide(headerLeft, headerRight),
    bodyTop:
      dp?.bodyTopPadding ??
      tp?.bodyTopPadding ??
      fromVariant("bodyTopPadding") ??
      bp?.bodyTopPadding ??
      DEFAULT_PADDING.bodyTop,
    bodyLeft,
    bodyRight,
    bodyBottom:
      dp?.bodyBottomPadding ??
      tp?.bodyBottomPadding ??
      fromVariant("bodyBottomPadding") ??
      bp?.bodyBottomPadding ??
      DEFAULT_PADDING.bodyBottom,
    bodySide: symmetricSide(bodyLeft, bodyRight),
    footerBottom:
      dp?.footerBottomPadding ??
      tp?.footerBottomPadding ??
      fromVariant("footerBottomPadding") ??
      bp?.footerBottomPadding ??
      DEFAULT_PADDING.footerBottom,
    footerLeft,
    footerRight,
    footerSide: symmetricSide(footerLeft, footerRight),
  };
}

/** A resolved padding re-expressed as a source, so it can seed a lower tier. */
export function paddingAsSource(p: DocumentPadding): PaddingSource {
  return {
    headerTopPadding: p.headerTop,
    headerLeftPadding: p.headerLeft,
    headerRightPadding: p.headerRight,
    bodyTopPadding: p.bodyTop,
    bodyLeftPadding: p.bodyLeft,
    bodyRightPadding: p.bodyRight,
    bodyBottomPadding: p.bodyBottom,
    footerBottomPadding: p.footerBottom,
    footerLeftPadding: p.footerLeft,
    footerRightPadding: p.footerRight,
  };
}

// ---------------------------------------------------------------------------
// Letterhead-measured body column (MOR-1432)
// ---------------------------------------------------------------------------

/**
 * The body side margins a page inherits from its letterhead: those measured
 * from the source page of the page-fit header showing on that page, else
 * of the page-fit footer. Slot-fit artwork is squeezed into the margins, so
 * its source column means nothing and is ignored. `null` when the page's
 * letterhead says nothing about the body.
 */
export function letterheadBodyPaddingForPage(
  variants: VariantFragments[],
  pageIndex: number,
): PaddingSource | null {
  const header = pickVariantForPage(variants, "header", pageIndex);
  const footer = pickVariantForPage(variants, "footer", pageIndex);
  const fromHeader =
    header?.headerPageFitTop !== null && header?.headerPageFitTop !== undefined
      ? header.sourceBodyMargins
      : null;
  const fromFooter =
    footer?.footerPageFitBottom !== null &&
    footer?.footerPageFitBottom !== undefined
      ? footer.sourceBodyMargins
      : null;
  const margins = fromHeader ?? fromFooter ?? null;
  if (!margins) return null;
  const left = margins.left ?? null;
  const right = margins.right ?? null;
  if (left === null && right === null) return null;
  return { bodyLeftPadding: left, bodyRightPadding: right };
}

/**
 * The ordered variant tier for `resolveDocumentPadding`: every variant's
 * own padding (highest priority first), then the body column the page's
 * letterhead measured from its source. Explicit variant, template, document
 * and page margins therefore all beat the letterhead, and the letterhead
 * beats the firm profile's generic defaults.
 */
export function variantPaddingTiers(
  variants: VariantFragments[],
  pageIndex = 1,
): Array<PaddingSource | null> {
  const own = variants.map((v) => v.padding ?? null);
  const letterhead = letterheadBodyPaddingForPage(variants, pageIndex);
  return letterhead ? [...own, letterhead] : own;
}

// ---------------------------------------------------------------------------
// Per-page padding resolution (per-layout variant margins + page overrides)
// ---------------------------------------------------------------------------

/** One page's stored override on a template or job document. */
export interface PageOverrideSource {
  page: number;
  padding: PaddingSource;
}

export interface PagePaddingInputs {
  brandingPadding?: PaddingSource | null;
  templatePadding?: PaddingSource | null;
  documentPadding?: PaddingSource | null;
  /** Variant fragments in priority order (same order used for zone picking). */
  variants: VariantFragments[];
  templatePageOverrides?: PageOverrideSource[] | null;
  documentPageOverrides?: PageOverrideSource[] | null;
}

export function layoutKeysEqual(a: PageLayoutKey, b: PageLayoutKey): boolean {
  return (
    a.header === b.header &&
    a.footer === b.footer &&
    a.leftSide === b.leftSide &&
    a.rightSide === b.rightSide
  );
}

/**
 * The layout key of a page: which asset (or none) each zone shows on it,
 * as decided by the same `pickVariantForPage` the renderers use.
 */
export function layoutKeyForPage(
  variants: VariantFragments[],
  pageIndex: number,
): PageLayoutKey {
  const h = pickVariantForPage(variants, "header", pageIndex);
  const f = pickVariantForPage(variants, "footer", pageIndex);
  const l = pickVariantForPage(variants, "leftSide", pageIndex);
  const r = pickVariantForPage(variants, "rightSide", pageIndex);
  return {
    header: h?.headerAssetId ?? null,
    footer: f?.footerAssetId ?? null,
    leftSide: l?.leftSideAssetId ?? null,
    rightSide: r?.rightSideAssetId ?? null,
  };
}

function pageOverrideFor(
  list: PageOverrideSource[] | null | undefined,
  pageIndex: number,
): PaddingSource | null {
  return list?.find((e) => e.page === pageIndex)?.padding ?? null;
}

/**
 * Per-page margin resolution. Tiers, first hit per field wins:
 *   1. document page override
 *   2. template page override
 *   3. a variant's `layoutMargins` entry whose key matches this page's zones
 *      (variants searched in priority order)
 *   4. `resolveDocumentPadding` (today's uniform tiers, with the page's
 *      letterhead-measured body column under the variants' own padding)
 *
 * When none of 1-3 apply the result is exactly the uniform resolution, so
 * documents that configure nothing new are unchanged. When they do apply,
 * the page tiers are laid in front of the uniform result by re-running the
 * uniform resolver with the resolved uniform values as its base, so the
 * per-side / symmetric / legacy fallback inside each tier is the existing
 * one (`tierSide`).
 */
export function resolvePagePadding(
  inputs: PagePaddingInputs,
  pageIndex: number,
): DocumentPadding {
  const uniform = resolveDocumentPadding(
    inputs.brandingPadding,
    inputs.templatePadding,
    inputs.documentPadding,
    variantPaddingTiers(inputs.variants, pageIndex),
  );

  const key = layoutKeyForPage(inputs.variants, pageIndex);
  const combo =
    inputs.variants
      .map((v) => v.layoutMargins?.find((e) => layoutKeysEqual(e.key, key)))
      .find((hit): hit is LayoutMarginEntry => hit !== undefined)?.padding ??
    null;
  const docPage = pageOverrideFor(inputs.documentPageOverrides, pageIndex);
  const tplPage = pageOverrideFor(inputs.templatePageOverrides, pageIndex);
  if (!docPage && !tplPage && !combo) return uniform;
  return resolveDocumentPadding(paddingAsSource(uniform), tplPage, docPage, [
    combo,
  ]);
}
